/**
 * Website i18n (Vue 2 / Webpack)
 * ─────────────────────────────
 * Fixed labels for the hosted website renderer (`Website/vue`) and the CMS
 * blocks it shares with the editor (`Application/websites/cms/blocks`).
 *
 * One language per website. `Website/index.php` inlines
 * `<script type="application/json" id="mediaos-i18n">` with
 * `{ language, locale, messages }` already merged over English, so this
 * module reads it synchronously. Inside the CMS editor (no script tag) it
 * falls back to the bundled English dictionary.
 *
 * Usage in a block:
 *   import { t } from '@/websites/i18n/websiteI18n';
 *   methods: { t },
 *   <button :aria-label="t('navigation.search')">
 */
import en from '../../../Resources/i18n/website/en.json';

export const DEFAULT_LANGUAGE = 'en';
export const SUPPORTED_LANGUAGES = ['en', 'es-MX', 'pt-BR'];

let active = null;

function readInline()
{
	if (typeof document === 'undefined')
		return null;
	let node = document.getElementById('mediaos-i18n');
	if (!node)
		return null;
	try
	{
		let data = JSON.parse(node.textContent || '{}');
		if (data && data.messages && typeof data.messages === 'object')
			return data;
	}
	catch (e)
	{
		// Malformed inline JSON: fall back to English below.
	}
	return null;
}

function resolve()
{
	if (active)
		return active;
	let inline = readInline();
	active = {
		language: inline?.language || DEFAULT_LANGUAGE,
		locale: inline?.locale || 'en-US',
		messages: inline?.messages || en,
	};
	return active;
}

/**
 * Forces a dictionary. Used by tests and by the editor preview.
 * @param {{language?: string, locale?: string, messages?: Object}|null} data
 */
export function setI18n(data)
{
	active = data ? {
		language: data.language || DEFAULT_LANGUAGE,
		locale: data.locale || 'en-US',
		messages: data.messages || en,
	} : null;
}

/** @returns {string} Active language code. */
export function currentLanguage()
{
	return resolve().language;
}

/** @returns {string} Active BCP-47 locale. */
export function currentLocale()
{
	return resolve().locale;
}

function lookup(messages, key)
{
	return key.split('.').reduce((node, part) => (node && typeof node === 'object') ? node[part] : undefined, messages);
}

function interpolate(text, params)
{
	if (!params)
		return text;
	return text.replace(/\{(\w+)\}/g, (match, name) => (params[name] === undefined || params[name] === null) ? match : String(params[name]));
}

function pluralForm(value, count)
{
	let category = 'other';
	try
	{
		category = new Intl.PluralRules(currentLocale()).select(count);
	}
	catch (e)
	{
		category = count === 1 ? 'one' : 'other';
	}
	return value[category] ?? value.other ?? value.one ?? '';
}

/**
 * Translates a dotted key. Missing keys fall back to English, then to the
 * key itself.
 * @param {string} key
 * @param {Object<string, *>} [params]
 * @returns {string}
 */
export function t(key, params)
{
	let value = lookup(resolve().messages, key);
	if (value === undefined)
		value = lookup(en, key);
	if (value === undefined)
		return key;

	if (typeof value === 'object')
	{
		let count = params && params.count !== undefined ? Number(params.count) : 0;
		value = pluralForm(value, count);
	}

	return interpolate(String(value), params);
}

/**
 * @param {Date|string|number} value
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}
 */
export function formatDate(value, options = { year: 'numeric', month: 'short', day: 'numeric' })
{
	let date = value instanceof Date ? value : new Date(value);
	if (isNaN(date.getTime()))
		return '';
	return new Intl.DateTimeFormat(currentLocale(), options).format(date);
}

/**
 * @param {number|string} value
 * @param {Intl.NumberFormatOptions} [options]
 * @returns {string}
 */
export function formatNumber(value, options = {})
{
	let number = Number(value);
	if (isNaN(number))
		return '';
	return new Intl.NumberFormat(currentLocale(), options).format(number);
}

/**
 * @param {number|string} value
 * @param {string} [currency]
 * @param {Intl.NumberFormatOptions} [options]
 * @returns {string}
 */
export function formatCurrency(value, currency = 'USD', options = {})
{
	return formatNumber(value, { style: 'currency', currency: currency || 'USD', ...options });
}
